import React, { useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Icon, Input, Row, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { RBAC } from 'src/config';
import { appActions } from 'src/store/actions';
import { Page, Rbac } from 'src/containers';
import { ClientsSelection, RolesDropdown, SiteRolesDropdown, Button } from 'src/components';
import { uuid } from 'src/functions';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_MEMBERSHIPS': {
      return {
        ...state,
        memberships: action.payload.map((m) => ({ ...m, key: uuid() })),
      };
    }
    case 'REMOVE_MEMBERSHIP': {
      return {
        ...state,
        memberships: state.memberships.filter(({ key }) => key !== action.payload.key),
      };
    }
    case 'ADD_MEMBERSHIP': {
      return {
        ...state,
        memberships: [...state.memberships, { key: uuid(), client: {}, role: {} }],
      };
    }
    case 'UPDATE_CLIENT': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, client: action.payload.client } : m
        ),
      };
    }
    case 'UPDATE_ROLE': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, role: action.payload.role } : m
        ),
      };
    }
    case 'UPDATE_SITE_ROLE': {
      return {
        ...state,
        siteRole: action.payload,
      };
    }
    default:
      return state;
  }
};

const defaultValues = {
  username: '',
  email: '',
  givenName: '',
  familyName: '',
  address: '',
  team: '',
  memberships: [],
  siteRole: '',
};

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function AddUser({
  clients,
  enqueueNotification,
  fetchClients,
  fetchClientsStatus,
  history,
  profile,
  register,
  registerError,
  registerStatus,
}) {
  const [state, dispatch] = useReducer(reducer, {
    memberships: [{ key: uuid(), client: clients?.length === 1 ? clients[0] : {}, role: {} }],
  });
  const { handleSubmit, errors, control, setValue, register: registerInput, reset, watch } = useForm({ defaultValues });
  const watchEmail = watch('email');

  /* eslint-disable no-param-reassign */
  const onSubmit = (data) => {
    register(data);
  };
  /* eslint-enable no-param-reassign */

  const registerInputs = useCallback(() => {
    registerInput(
      { name: 'memberships', type: 'custom' },
      {
        validate: (memberships) =>
          (memberships?.every((m) => m.client && m.client.tenant && m.role && m.role.name) ?? false) ||
          'There are error(s) in the client membership',
      }
    );
    registerInput(
      { name: 'siteRole', type: 'custom' },
      {
        // NOTE: watch needs to be used directly here due to registerInputs only called once on load. The current value of email must be checked during validation.
        validate: (value) =>
          ['', null, undefined, CONSTANTS.ROLE.CLIENT_USER].includes(value?.name) ||
          (CONSTANTS.SITE_ROLES.slice(0, -1).includes(value?.name) && watch('email').match(/@pinpoint\.ai$/)) ||
          'User must have a Pinpoint email address',
      }
    );
  }, [registerInput, watch]);

  const checkClientEmailWhitelist = useCallback(
    (email) => {
      const whitelist = state.memberships.map(({ client }) => client.emailWhitelist);
      const whitelistCs = whitelist.join(',');
      // wildcard check left for backwards compatibility
      if (whitelistCs.includes('*')) return true;
      if (RBAC.CAN_UPDATE_USER_SITE_ROLE.includes(state?.siteRole?.name)) return true;

      const whitelistCombined = whitelistCs.split(',').filter((elem) => ![null, undefined, ''].includes(elem));
      const emailDomain = email.split('@')[1];
      return (
        whitelistCombined.some((allowedDomain) => allowedDomain === emailDomain) ||
        `Email address is not whitelisted. Allowed domains: ${whitelistCombined.join(',')}`
      );
    },
    [state]
  );

  const validateUsername = useCallback((username) => {
    const isEmail = (v) => emailRegex.test(v.trim());
    const includesSymbols = (v) => /[@]/.test(v.trim());
    return (!isEmail(username) && !includesSymbols(username)) || 'Username cannot be an email address';
  }, []);

  useEffect(() => {
    setValue('memberships', state.memberships, { shouldValidate: false });
  }, [state.memberships, setValue]);

  useEffect(() => {
    setValue('siteRole', state.siteRole, { shouldValidate: true });
  }, [state.siteRole, setValue, watchEmail]);

  useEffect(() => {
    fetchClients();
    registerInputs();
    // NOTE: needs to run once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (registerStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(registerError);
    }
    if (registerStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('User successfully registered', { autoClose: true });
      setTimeout(() => {
        reset(defaultValues);
        registerInputs();
      }, 2000);
    }
  }, [registerStatus, registerError, enqueueNotification, registerInputs, reset]);

  return (
    <Container>
      <Row p={{ t: '1.25rem' }}>
        <Col d="flex" size="6" gutterWidth="1.25rem">
          <Div>
            <Button
              buttonStyle="primary"
              onClick={() => {
                history.push('/users');
              }}
              data-tip="go back"
              data-test="back-btn"
            >
              <Icon color="white" name="LongLeft" size="1rem" m={{ r: '.5em' }} />
              BACK
            </Button>
          </Div>
        </Col>
      </Row>
      <Div bg="white" rounded="md" m={{ y: '1.25rem' }} p={{ x: '1.5rem', y: '1.25rem' }}>
        <Container>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ b: '1rem' }} data-test="username-row">
              <Col size={{ xs: '12', md: '2' }}>
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Username</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }} data-test="username-value">
                <Controller
                  name="username"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Username is required',
                    validate: validateUsername,
                  }}
                  data-test="username-input"
                />
              </Col>
            </Row>
            <Row data-test="username-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="username" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="email-row">
              <Col size={{ xs: '12', md: '2' }} data-test="email-label">
                <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                  <Text textSize="subtitle">Email</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }} data-test="email-value">
                <Controller
                  name="email"
                  as={Input}
                  control={control}
                  disabled={state.memberships.length > 0 && !(Object.keys(state.memberships[0].client).length > 0)}
                  rules={{
                    validate: checkClientEmailWhitelist,
                    required: 'Email is required',
                    pattern: {
                      value: emailRegex,
                      message: 'The email is invalid',
                    },
                  }}
                  placeholder={
                    state.memberships.length > 0 && !(Object.keys(state.memberships[0].client).length > 0)
                      ? 'Please select a client'
                      : null
                  }
                  data-test="email-input"
                />
              </Col>
            </Row>
            <Row data-test="email-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="email" />
                </Text>
              </Col>
            </Row>

            <Rbac roles={RBAC.CAN_UPDATE_USER_SITE_ROLE}>
              <Row p={{ b: '1rem' }} data-test="siteRole-row">
                <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="siteRole-label">
                  <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                    <Text textSize="subtitle">Site Role</Text>
                  </Div>
                </Col>
                <Col
                  d="flex"
                  align="center"
                  size={{ xs: '12', md: '10' }}
                  gutterWidth="1.25rem"
                  data-test="siteRole-label"
                >
                  <SiteRolesDropdown
                    w="100%"
                    defaultValue="Please select a role"
                    onSelectRole={(roleName) => {
                      const nextRole = { name: roleName };
                      dispatch({
                        type: 'UPDATE_SITE_ROLE',
                        payload: nextRole,
                      });
                    }}
                    currentRole={profile.siteRole || profile.role}
                    dropDownStyle={{
                      'data-test': 'siteRole-dropdown',
                    }}
                  />
                </Col>
              </Row>
              <Row data-test="siteRole-row-error">
                <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                  <Text textColor="danger800" textSize="caption">
                    <ErrorMessage errors={errors} name="siteRole" />
                  </Text>
                </Col>
              </Row>
            </Rbac>

            <Row p={{ b: '1rem' }} data-test="givenName-row">
              <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="givenName-label">
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">First Name</Text>
                </Div>
              </Col>
              <Col
                d="flex"
                align="center"
                size={{ xs: '12', md: '10' }}
                gutterWidth="1.25rem"
                data-test="givenName-value"
              >
                <Controller
                  name="givenName"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'First name is required',
                  }}
                  data-test="givenName-input"
                />
              </Col>
            </Row>
            <Row data-test="givenName-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="givenName" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="familyName-row">
              <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="familyName-label">
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Last Name</Text>
                </Div>
              </Col>
              <Col
                d="flex"
                align="center"
                size={{ xs: '12', md: '10' }}
                gutterWidth="1.25rem"
                data-test="familyName-value"
              >
                <Controller
                  name="familyName"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Last name is required',
                  }}
                  data-test="familyName-input"
                />
              </Col>
            </Row>
            <Row data-test="familyName-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="familyName" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="team-row">
              <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="team-label">
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Team</Text>
                </Div>
              </Col>
              <Col d="flex" align="center" size={{ xs: '12', md: '10' }} gutterWidth="1.25rem" data-test="team-value">
                <Controller name="team" as={Input} control={control} rules={{}} data-test="team-input" />
              </Col>
            </Row>
            <Row data-test="team-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="team" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="address-row">
              <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="address-label">
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Address</Text>
                </Div>
              </Col>
              <Col
                d="flex"
                align="center"
                size={{ xs: '12', md: '10' }}
                gutterWidth="1.25rem"
                data-test="address-value"
              >
                <Controller
                  name="address"
                  as={Input}
                  control={control}
                  rules={{
                    required: 'Address is required',
                  }}
                  data-test="address-input"
                />
              </Col>
            </Row>
            <Row data-test="address-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="address" />
                </Text>
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="membership-row">
              <Col size={{ xs: '12', md: '2' }} gutterWidth="1.25rem" data-test="membership-label">
                <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                  <Text textSize="subtitle">Clients</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '10' }} gutterWidth="1.25rem" data-test="membership-value">
                {state.memberships.map(({ client, role, key }, idx) => (
                  <Row d="flex" align="center" p={{ b: '1rem' }} data-test={`membership-selection-row-${idx}`}>
                    <Col
                      size={{ xs: '12', md: '6' }}
                      gutterWidth="1.25rem"
                      data-test={`membership-selection-clients-${idx}`}
                    >
                      <ClientsSelection
                        w="100%"
                        loading={fetchClientsStatus === CONSTANTS.API.STARTED}
                        clients={clients}
                        tenant={client && client.tenant}
                        defaultText="Please select a client"
                        onSelectClient={(nextClient) => {
                          dispatch({
                            type: 'UPDATE_CLIENT',
                            payload: {
                              client: nextClient,
                              key,
                            },
                          });
                        }}
                      />
                    </Col>
                    <Col
                      size={{ xs: '12', md: '5' }}
                      gutterWidth="1.25rem"
                      data-test={`membership-selection-roles-${idx}`}
                    >
                      <RolesDropdown
                        w="100%"
                        defaultValue={CONSTANTS.ROLE_RENAMED[role.name] || role.name || 'Please select a role'}
                        onSelectRole={(roleName) => {
                          const nextRole = { name: roleName };
                          dispatch({
                            type: 'UPDATE_ROLE',
                            payload: {
                              role: nextRole,
                              key,
                            },
                          });
                        }}
                        currentRole={profile.role}
                        dropDownStyle={{
                          'data-test': 'role-dropdown',
                        }}
                      />
                    </Col>
                    <Col
                      size={{ xs: '12', md: '1' }}
                      gutterWidth="1.25rem"
                      data-test={`membership-selection-delete-${idx}`}
                    >
                      <Button
                        data-test={`membership-selection-delete-btn-${idx}`}
                        buttonStyle="primary"
                        disabled={state.memberships.length === 1}
                        onClick={() => {
                          dispatch({
                            type: 'REMOVE_MEMBERSHIP',
                            payload: {
                              key,
                            },
                          });
                        }}
                      >
                        <Icon color="white" name="Delete" size="1rem" />
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Col>
            </Row>
            <Row p={{ b: '1rem' }} data-test="membership-row-error">
              <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="memberships" />
                </Text>
              </Col>
            </Row>
            <Row>
              <Col offset="2" size={{ xs: '12', md: '2' }} gutterWidth="1.25rem">
                <Button
                  buttonStyle="primary"
                  onClick={() => {
                    dispatch({
                      type: 'ADD_MEMBERSHIP',
                    });
                  }}
                  data-test="add-membership-btn"
                >
                  <Icon name="Plus" color="white" m={{ r: '0.5rem' }} size="1rem" />
                  ADD MEMBERSHIP
                </Button>
              </Col>
            </Row>
            <Row d="flex" justify="flex-end" p={{ b: '1rem' }}>
              <Col>
                <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)} data-test="submit-btn">
                  <Text color="gray400">SAVE</Text>
                  {registerStatus === CONSTANTS.API.STARTED ? (
                    <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} />
                  ) : null}
                </Button>
              </Col>
            </Row>
          </form>
        </Container>
      </Div>
    </Container>
  );
}

const mapStateToProps = ({ app, users }) => ({
  ...app,
  ...users,
});

const mapDispatchToProps = (dispatch) => ({
  fetchClients: () => dispatch(appActions.fetchClients()),
  register: (payload) => dispatch(appActions.register(payload)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

AddUser.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchClientsStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  registerStatus: PropTypes.string.isRequired,
  registerError: PropTypes.string.isRequired,
  fetchClients: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    role: PropTypes.string,
    siteRole: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(AddUser));
